import { useEffect, useState } from 'react';
import { buildExternalBrowserUrl } from '@/components/InAppBrowserNotice/externalBrowserUrl';
import { InAppBrowserNotice } from '@/components/InAppBrowserNotice/InAppBrowserNotice';
import {
  dismissInAppBrowserNotice,
  isInAppBrowserNoticeDismissed,
} from '@/components/InAppBrowserNotice/inAppBrowserNoticeSession';
import { useInAppBrowserNoticeDeterminer } from '@/hooks/featureManagers/useInAppBrowserNoticeDeterminer';
import { AmplitudeHelper } from '@/utils/amplitudeHelper';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { detectPlatform } from '@/utils/detectPlatform';
import { isNullish } from '@/utils/isNullish';

/**
 * Shows the in-app browser notice on whichever page the learner lands on first, once per browser session.
 * Only closing it dismisses it: after open / copy the learner may still be here because
 * the hand-off failed.
 */
export const InAppBrowserNoticeGate = () => {
  const [platform] = useState(detectPlatform);
  const isNoticeEnabled = useInAppBrowserNoticeDeterminer();
  const [isDismissed, setIsDismissed] = useState(isInAppBrowserNoticeDismissed);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const { os, inApp } = platform;

  const open = isNoticeEnabled && !isNullish(inApp) && !isDismissed;
  const externalUrl = buildExternalBrowserUrl(platform, window.location.href);

  useEffect(() => {
    if (open && !isNullish(inApp)) {
      AmplitudeHelper.trackInAppBrowserNoticeShown({ in_app: inApp, os, user_agent: navigator.userAgent });
    }
  }, [open, inApp, os]);

  const trackAction = (action: 'open' | 'copy' | 'continue') => {
    if (!isNullish(inApp)) {
      AmplitudeHelper.trackInAppBrowserNoticeAction({ in_app: inApp, os, user_agent: navigator.userAgent, action });
    }
  };

  const handleOpenExternal = () => {
    if (isNullish(externalUrl)) {
      return;
    }
    trackAction('open');
    window.location.assign(externalUrl);
  };

  const handleCopyLink = async () => {
    trackAction('copy');
    await copyToClipboard(window.location.href);
    setIsLinkCopied(true);
  };

  const handleClose = () => {
    trackAction('continue');
    dismissInAppBrowserNotice();
    setIsDismissed(true);
  };

  return (
    <InAppBrowserNotice
      open={open}
      link={window.location.href}
      canOpenExternal={!isNullish(externalUrl)}
      isLinkCopied={isLinkCopied}
      onOpenExternal={handleOpenExternal}
      onCopyLink={handleCopyLink}
      onClose={handleClose}
    />
  );
};
